import { forwardRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@rtkwlf/pro-solid-svg-icons/faCheck';
import { faCircleArrowDown } from '@rtkwlf/pro-solid-svg-icons/faCircleArrowDown';
import { faDash } from '@rtkwlf/pro-solid-svg-icons/faDash';
import { faDiamondExclamation } from '@rtkwlf/pro-solid-svg-icons/faDiamondExclamation';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { faHexagonExclamation } from '@rtkwlf/pro-solid-svg-icons/faHexagonExclamation';
import { faSparkles } from '@rtkwlf/pro-solid-svg-icons/faSparkles';

import { type Theme } from '../../styles';
import { Chip, type ChipProps } from '../index';

type VariantOption =
  | 'critical'
  | 'high'
  | 'medium'
  | 'unassigned'
  | 'low'
  | 'none'
  | 'new';

const defaultIconMapping: Record<VariantOption, JSX.Element> = {
  critical: (
    <FontAwesomeIcon data-testid="critical-icon" icon={faExclamationTriangle} />
  ),
  high: <FontAwesomeIcon data-testid="high-icon" icon={faDiamondExclamation} />,
  medium: (
    <FontAwesomeIcon data-testid="medium-icon" icon={faHexagonExclamation} />
  ),
  unassigned: <FontAwesomeIcon data-testid="unassigned-icon" icon={faDash} />,
  low: <FontAwesomeIcon data-testid="low-icon" icon={faCircleArrowDown} />,
  none: <FontAwesomeIcon data-testid="none-icon" icon={faCheck} />,
  new: <FontAwesomeIcon data-testid="new-icon" icon={faSparkles} />,
};

type ChipBaseProps = Pick<ChipProps, 'size' | 'sx'>;

export interface BaseStatusChipProps extends ChipBaseProps {
  variant: VariantOption;
  label: string | number;
  hideIcon?: boolean;
}

export const BaseStatusChip = forwardRef<HTMLDivElement, BaseStatusChipProps>(
  ({ variant, label, hideIcon, ...rest }: BaseStatusChipProps, ref) => {
    return (
      <Chip
        ref={ref}
        icon={!hideIcon ? defaultIconMapping[variant] : undefined}
        label={label}
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.status[variant],
          color: theme.palette.common.darkText,
          fontSize: theme.typography.caption,

          '.MuiChip-icon': {
            color: theme.palette.common.darkIcon,
          },
        })}
        {...rest}
      />
    );
  },
);
